import { useMemo, useState } from "react";
import {
  REDUNDANCY,
  TRIBUNAL,
  TAX_YEAR,
  gbp,
  redundancyRateForAge,
} from "@/lib/statutory";

export function UnfairDismissalCalculator() {
  const [age, setAge] = useState(38);
  const [yearsService, setYearsService] = useState(5);
  const [weeklyPay, setWeeklyPay] = useState(650);
  const [annualLoss, setAnnualLoss] = useState(35_000);
  const [monthsToNewJob, setMonthsToNewJob] = useState(6);
  const [acasUplift, setAcasUplift] = useState(0);

  const result = useMemo(() => {
    const cappedWeekly = Math.min(weeklyPay, REDUNDANCY.weeklyPayCap);
    const cappedYears = Math.min(yearsService, REDUNDANCY.serviceYearsCap);
    // Basic award: same formula as statutory redundancy.
    let basic = 0;
    for (let y = 1; y <= cappedYears; y++) {
      const ageDuringYear = age - (cappedYears - y);
      basic += redundancyRateForAge(ageDuringYear) * cappedWeekly;
    }
    // Compensatory: loss of earnings during unemployment, capped.
    const rawCompensatory = (annualLoss / 12) * monthsToNewJob;
    const statutoryCap = Math.min(
      TRIBUNAL.unfairDismissalCompensatoryCap,
      weeklyPay * 52,
    );
    const compensatory = Math.min(rawCompensatory, statutoryCap);
    const uplift = (basic + compensatory) * Math.min(acasUplift, TRIBUNAL.acasCodeUpliftMax);
    return { basic, compensatory, uplift, total: basic + compensatory + uplift, statutoryCap };
  }, [age, yearsService, weeklyPay, annualLoss, monthsToNewJob, acasUplift]);

  return (
    <div className="rounded-3xl border border-border bg-card p-8 shadow-sm sm:p-10">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <NumField label="Age at dismissal" value={age} onChange={setAge} min={16} max={80} />
        <NumField label="Complete years of service" value={yearsService} onChange={setYearsService} min={0} max={50} />
        <NumField label="Gross weekly pay (£)" value={weeklyPay} onChange={setWeeklyPay} min={0} />
        <NumField label="Net annual loss (£)" value={annualLoss} onChange={setAnnualLoss} min={0} />
        <NumField label="Months to find new job" value={monthsToNewJob} onChange={setMonthsToNewJob} min={0} max={24} />
        <NumField
          label="ACAS uplift (0–0.25)"
          value={acasUplift}
          onChange={setAcasUplift}
          min={0}
          max={TRIBUNAL.acasCodeUpliftMax}
          step={0.05}
        />
      </div>

      <div className="mt-8 rounded-2xl bg-brand p-6 text-brand-foreground">
        <p className="text-xs font-semibold uppercase tracking-widest text-brand-foreground/60">
          Estimated tribunal award
        </p>
        <p className="mt-3 font-serif text-5xl">{gbp(result.total, { decimals: 2 })}</p>
        <p className="mt-3 text-sm text-brand-foreground/70">
          Basic award {gbp(result.basic, { decimals: 2 })} · Compensatory{" "}
          {gbp(result.compensatory, { decimals: 2 })} · ACAS uplift {gbp(result.uplift, { decimals: 2 })}.
        </p>
      </div>

      <div className="mt-6 rounded-xl border border-border bg-background p-5">
        <h3 className="cursor-pointer text-sm font-medium text-brand">How this is calculated</h3>
        <div className="mt-4 space-y-3 text-sm text-muted-foreground">
          <p>
            The basic award mirrors the statutory redundancy formula: weeks per year of service depending on age, with
            weekly pay capped at {gbp(REDUNDANCY.weeklyPayCap)} ({TAX_YEAR}).
          </p>
          <p>
            The compensatory award reflects loss of earnings and benefits since dismissal, capped at the lower of 52
            weeks' actual pay or {gbp(TRIBUNAL.unfairDismissalCompensatoryCap)}. A tribunal can uplift the total by up
            to {Math.round(TRIBUNAL.acasCodeUpliftMax * 100)}% for unreasonable failure to follow the ACAS Code.
          </p>
          <p className="text-xs">
            Estimates only, every case turns on its facts. Always take legal advice before settling or defending a
            claim.
          </p>
        </div>
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, min, max, step }: { label: string; value: number; onChange: (n: number) => void; min?: number; max?: number; step?: number }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-foreground">{label}</span>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-base focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
      />
    </label>
  );
}
